import React, { useMemo, useRef } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { InfoIcon } from 'icons/materialIcons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    ChartTooltip,
    Legend
);

// Configure defaults for charts
ChartJS.defaults.scale.grid.drawOnChartArea = true;
ChartJS.defaults.maintainAspectRatio = false;

const ViewsOverTimeChartJS = ({ data = [], dropdown = null }) => {
    const chartRef = useRef();

    // Helper function to safely format dates
    const formatDate = (dateInput) => {
        if (!dateInput) return '';

        try {
            const date = new Date(dateInput);
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', error, dateInput);
            return 'Invalid Date';
        }
    };    // Create default data for empty state - with view types
    const getDefaultData = () => {
        const now = new Date();
        const startDate = new Date(now);
        startDate.setDate(now.getDate() - 29); // Start from 30 days ago
        const defaultData = [];

        // Generate data points for each day
        for (let date = new Date(startDate); date <= now; date.setDate(date.getDate() + 1)) {
            defaultData.push({
                date: formatDate(date),
                accessory: 0,
                qr_code: 0,
                share_link: 0,
                total: 0
            });
        }

        return defaultData;
    };

    // Process raw data by view type
    const processRawData = (rawData) => {
        if (rawData.length > 0 && (rawData[0].sourceType || rawData[0].timestamp)) {
            const dailyData = new Map();

            rawData.forEach(item => {
                const dateStr = formatDate(item.timestamp || item.date || item.createdAt);
                if (!dateStr || dateStr === 'Invalid Date') return;

                if (!dailyData.has(dateStr)) {
                    dailyData.set(dateStr, {
                        date: dateStr,
                        accessory: 0,
                        qr_code: 0,
                        share_link: 0,
                        total: 0
                    });
                }

                const entry = dailyData.get(dateStr);
                const sourceType = item.sourceType || 'qr_code';

                if (sourceType === 'accessory') {
                    entry.accessory += 1;
                } else if (sourceType === 'qr_code') {
                    entry.qr_code += 1;
                } else if (sourceType === 'share_link') {
                    entry.share_link += 1;
                }
                // Calculate total as sum of all sources
                entry.total = entry.accessory + entry.qr_code + entry.share_link;
            });

            const sortedData = Array.from(dailyData.values()).sort((a, b) => {
                return new Date(a.date) - new Date(b.date);
            });

            // Convert to cumulative totals (running sum)
            let cumulativeTotal = 0;
            return sortedData.map(item => {
                cumulativeTotal += item.total;
                return {
                    ...item,
                    total: cumulativeTotal
                };
            });
        }

        // If rawData is already processed, convert to cumulative
        if (Array.isArray(rawData) && rawData.length > 0 && rawData[0].total !== undefined) {
            let cumulativeTotal = 0;
            return rawData.map(item => {
                const itemTotal = (item.accessory || 0) + (item.qr_code || 0) + (item.share_link || 0);
                cumulativeTotal += itemTotal || item.total || 0;
                return {
                    ...item,
                    total: cumulativeTotal
                };
            });
        }

        return rawData;
    };

    // Convert byType counts to chart data
    const createDataFromCounts = (byType = {}) => {
        const now = new Date();
        const dateStr = formatDate(now);

        return [{
            date: dateStr,
            accessory: byType.accessory || 0,
            qr_code: byType.qr_code || 0,
            share_link: byType.share_link || 0,
            total: (byType.accessory || 0) + (byType.qr_code || 0) + (byType.share_link || 0)
        }];
    };

    // Get chart data with fallbacks and convert to cumulative
    const chartData = useMemo(() => {
        let processed = [];

        if (!data || (Array.isArray(data) && data.length === 0)) {
            processed = getDefaultData();
        } else if (data.byType && !Array.isArray(data)) {
            if (!data.overTime || data.overTime.length === 0) {
                processed = createDataFromCounts(data.byType);
            } else {
                processed = processRawData(data.overTime);
            }
        } else {
            processed = processRawData(data);
        }

        // Ensure cumulative totals (processRawData already handles this, but double-check)
        if (processed.length > 0 && processed[0].total !== undefined) {
            let cumulativeTotal = 0;
            return processed.map(item => {
                const itemTotal = (item.accessory || 0) + (item.qr_code || 0) + (item.share_link || 0);
                // If total is already cumulative from processRawData, use it; otherwise calculate
                cumulativeTotal = item.total || (cumulativeTotal + itemTotal);
                return {
                    ...item,
                    total: cumulativeTotal
                };
            });
        }

        return processed;
    }, [data]);    // Calculate totals for all view types
    const totals = useMemo(() => {
        if (!chartData || chartData.length === 0) {
            return {
                total: 0,
                accessory: 0,
                qr_code: 0,
                share_link: 0
            };
        }

        const result = chartData.reduce((acc, item) => {
            acc.accessory += item.accessory || 0;
            acc.qr_code += item.qr_code || 0;
            acc.share_link += item.share_link || 0;
            return acc;
        }, {
            total: 0,
            accessory: 0,
            qr_code: 0,
            share_link: 0
        });

        // Calculate total as sum of all sources to ensure accuracy
        result.total = result.accessory + result.qr_code + result.share_link;

        return result;
    }, [chartData]);    // Prepare Chart.js data - showing only total views as one line
    const chartJSData = {
        labels: chartData.map(item => item.date),
        datasets: [
            {
                label: 'Views',
                data: chartData.map(item => {
                    // Calculate total as sum of all sources to ensure accuracy
                    const total = (item.accessory || 0) + (item.qr_code || 0) + (item.share_link || 0);
                    return item.total || total;
                }),
                backgroundColor: (context) => {
                    const ctx = context.chart.ctx;
                    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
                    gradient.addColorStop(0, 'rgba(41, 174, 248, 0.25)');
                    gradient.addColorStop(1, 'rgba(41, 174, 248, 0.05)');
                    return gradient;
                },
                borderColor: '#29AEF8',
                borderWidth: 3,
                borderCapStyle: 'round',
                borderJoinStyle: 'miter',
                pointRadius: 0,
                pointHoverRadius: 7,
                pointBackgroundColor: '#29AEF8',
                pointBorderColor: '#FFFFFF',
                pointBorderWidth: 3,
                pointHoverBackgroundColor: '#29AEF8',
                pointHoverBorderColor: '#FFFFFF',
                pointHoverBorderWidth: 3,
                fill: true,
                tension: 0,
                stepped: false,
                yAxisID: 'y',
            }
        ]
    };

    // Enhanced multi-axis chart options with improved visuals
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 1200,
            easing: 'easeInOutQuart',
            x: {
                duration: 0
            },
            y: {
                duration: 1200,
                easing: 'easeInOutQuart'
            }
        },
        interaction: {
            mode: 'index',
            intersect: false,
        },
        layout: {
            padding: {
                top: 10,
                right: 15,
                bottom: 5,
                left: 5
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                padding: 12,
                titleFont: {
                    size: 13,
                    weight: '600',
                    family: "'Inter', 'Roboto', sans-serif"
                },
                bodyFont: {
                    size: 12,
                    family: "'Inter', 'Roboto', sans-serif"
                },
                titleColor: '#FFFFFF',
                bodyColor: '#FFFFFF',
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        label += context.parsed.y || 0;
                        return label;
                    }
                }
            }
        },
        scales: {
            x: {
                type: 'category',
                position: 'bottom',
                grid: {
                    display: true,
                    drawOnChartArea: true,
                    drawBorder: false,
                    color: 'rgba(0, 0, 0, 0.05)',
                    lineWidth: 1
                },
                border: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)',
                    width: 1
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 0,
                    color: '#6B7280',
                    font: {
                        size: 11,
                        family: "'Inter', 'Roboto', sans-serif",
                        weight: '400'
                    },
                    autoSkip: true,
                    maxTicksLimit: 10,
                    padding: 8
                }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                beginAtZero: true,
                grid: {
                    display: true,
                    drawBorder: false,
                    color: 'rgba(0, 0, 0, 0.05)',
                    lineWidth: 1
                },
                border: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)',
                    width: 1
                },
                ticks: {
                    stepSize: 1,
                    precision: 0,
                    color: '#6B7280',
                    font: {
                        size: 11,
                        family: "'Inter', 'Roboto', sans-serif",
                        weight: '400'
                    },
                    padding: 8
                },
                title: {
                    display: false
                }
            }
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '414px',
                position: 'relative',
                borderRadius: '18px',
                backgroundColor: '#FFFFFF'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    padding: '32px 18px 26px 18px',
                    flexDirection: 'column'
                }}
            >
                {/* Header section */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#333333',
                        fontSize: '12px',
                        gap: '5px'
                    }}
                >
                    {/* Dropdown as heading for team insights */}
                    {dropdown || (
                        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                                Views Over Time
                            </Typography>
                            <Tooltip title="Total profile views over time">
                                <InfoIcon sx={{ fontSize: 16, color: '#828282' }} />
                            </Tooltip>
                        </Box>
                    )}
                </Box>
                {/* Enhanced Legend */}
                <Box
                    sx={{
                        display: 'flex',
                        padding: '24px 0 20px 0',
                        gap: '20px',
                        flexWrap: 'wrap',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                        marginBottom: '16px'
                    }}
                >                    {[
                    { label: 'Views', value: totals.total, color: '#29AEF8' }
                ].map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <Box
                            sx={{
                                width: '16px',
                                height: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: item.color,
                                borderRadius: '4px'
                            }}
                        >
                            <svg width="16" height="14" viewBox="0 0 8 6" fill="none">
                                <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Box>
                        <Typography sx={{
                            fontSize: '13px',
                            color: '#374151',
                            fontWeight: '500',
                            fontFamily: "'Inter', 'Roboto', sans-serif"
                        }}>
                            {item.label} <span style={{ color: '#6B7280', fontWeight: '400' }}>({item.value})</span>
                        </Typography>
                    </Box>
                ))}
                </Box>

                {/* Chart */}
                <Box
                    sx={{
                        height: '260px',
                        width: '100%',
                        marginTop: '8px',
                        position: 'relative'
                    }}
                >                    <Line
                        ref={chartRef}
                        data={chartJSData}
                        options={options}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default ViewsOverTimeChartJS;
